import type { LedgerFact } from '../../Insights/LeadEngagementLedger';
import type { CadenceEvaluation } from './cadence-timeline';
import { isIncomingCall } from './lead-conversation-call';
import type { TraceRef } from './trace-link';

export interface EngagementMessage {
  id?: string;
  message: string;
  incoming: boolean;
  sender_role?: 'lead' | 'ai' | 'agent' | 'other';
  text_conversation_id?: string | null;
  created_at: string;
  delivery_status?: string | null;
  error_code?: string | null;
  trace_ids?: TraceRef[] | null;
}

export interface ThreeWayConversation {
  agentName: string | null;
  messages: EngagementMessage[];
}

export interface EngagementCall {
  id?: string;
  created_at: string;
  inbound?: boolean | null;
  incoming?: boolean | null;
  duration?: number | null;
  is_voicemail?: boolean;
  transcript?: string | null;
  call_type?: string | null;
  recording_url?: string | null;
  external_conversation_id?: string | null;
  post_call_analysis_result?: {
    call_result?: string | null;
  } | null;
}

export interface RoutingAttempt {
  id?: string;
  agent_id?: string;
  agent_name?: string;
  answered_at?: string | null;
  was_routed?: boolean;
  call_status?: string | null;
  created_at?: string | null;
}

export interface RoutingCall {
  call_id?: string;
  call_source?: string | null;
  call_outcome?: string | null;
  created_at: string;
  routing_started_at?: string | null;
  routing_completed_at?: string | null;
  recording_url?: string | null;
  external_conversation_id?: string | null;
  routed_agent?: {
    first_name?: string;
    last_name?: string;
  } | null;
  attempts: RoutingAttempt[];
}

export interface EngagementNote {
  id?: string;
  header?: string | null;
  body: string;
  created_at: string;
  created_by?: string | null;
}

export interface IsaText {
  sent_at: string;
  from_role: string | null;
  body: string;
}

export interface IsaOffer {
  id: string;
  agent_name: string | null;
  wave_number: number;
  status: string;
  offered_at: string;
  responded_at: string | null;
  texts: IsaText[];
}

export interface IsaWorkflow {
  id: string;
  origin: string | null;
  status: string;
  started_at: string;
  completed_at: string | null;
  claimed_by_agent_name: string | null;
  winner_notified_at: string | null;
  claim_intro_sent_at: string | null;
  crm_appointment_id: string | null;
  appointment_start: string | null;
  offers: IsaOffer[];
}

export interface EngagementTimelineSources {
  messages: EngagementMessage[];
  calls: EngagementCall[];
  routing: RoutingCall[];
  cadenceEvaluations: CadenceEvaluation[];
  ledgerFacts: LedgerFact[];
  notes: EngagementNote[];
  isaWorkflows: IsaWorkflow[];
}

export type EngagementTimelineEvent =
  | {
      source: 'message';
      timestamp: string;
      key: string;
      payload: EngagementMessage;
    }
  | {
      source: 'call';
      timestamp: string;
      key: string;
      payload: EngagementCall;
    }
  | {
      source: 'routing';
      timestamp: string;
      key: string;
      payload: RoutingCall;
    }
  | {
      source: 'cadence';
      timestamp: string;
      key: string;
      payload: CadenceEvaluation;
    }
  | {
      source: 'ledger';
      timestamp: string;
      key: string;
      payload: LedgerFact;
    }
  | {
      source: 'note';
      timestamp: string;
      key: string;
      payload: EngagementNote;
    }
  | {
      source: 'isa';
      timestamp: string;
      key: string;
      payload: IsaWorkflow;
    }
  | {
      source: 'threeWay';
      timestamp: string;
      key: string;
      payload: ThreeWayConversation;
    };

type MessageTimelineEvent = Extract<
  EngagementTimelineEvent,
  { source: 'message' }
>;
type CallTimelineEvent = Extract<EngagementTimelineEvent, { source: 'call' }>;

export interface CadenceOutreachAssociation {
  expectsCall: boolean;
  expectsMessage: boolean;
  message?: MessageTimelineEvent;
  call?: CallTimelineEvent;
}

const hasTimezone = (timestamp: string): boolean =>
  /(?:[Zz]|[+-]\d{2}(?::?\d{2})?)$/.test(timestamp);

export const parseTimelineTimestamp = (timestamp: string): number =>
  Date.parse(hasTimezone(timestamp) ? timestamp : `${timestamp}Z`);

const canonicalize = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(canonicalize).sort((left, right) => {
      const leftKey = JSON.stringify(left);
      const rightKey = JSON.stringify(right);
      return leftKey < rightKey ? -1 : leftKey > rightKey ? 1 : 0;
    });
  }
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce<Record<string, unknown>>((result, key) => {
        result[key] = canonicalize((value as Record<string, unknown>)[key]);
        return result;
      }, {});
  }
  return value;
};

const stablePayloadKey = (payload: unknown): string =>
  JSON.stringify(canonicalize(payload)) ?? String(payload);

const timelineKey = (
  source: EngagementTimelineEvent['source'],
  id: string | undefined,
  payload: unknown,
): string => `${source}:${id || stablePayloadKey(payload)}`;

const compareText = (left: string, right: string): number =>
  left < right ? -1 : left > right ? 1 : 0;

export const compareTimelineEvents = (
  left: EngagementTimelineEvent,
  right: EngagementTimelineEvent,
): number => {
  const timestampDifference =
    parseTimelineTimestamp(right.timestamp) -
    parseTimelineTimestamp(left.timestamp);
  if (timestampDifference !== 0) {
    return timestampDifference;
  }
  const sourceDifference = compareText(left.source, right.source);
  return sourceDifference || compareText(left.key, right.key);
};

const ISA_RESULT_LABELS: Record<string, string> = {
  claim_won: 'Claimed',
  claim_lost: 'Too late',
  rejected: 'Declined',
  expired: 'No reply',
  offered: 'Pending',
  delivery_failed: 'Not delivered',
};

export const sentenceCase = (value: string): string => {
  const label = value.replace(/[-_]/g, ' ');
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
};

export const isaResultLabel = (status: string): string =>
  ISA_RESULT_LABELS[status] ?? sentenceCase(status);

export const isaOfferResultLabel = (offer: IsaOffer): string =>
  offer.status === 'claim_lost' && !isaReplyAt(offer)
    ? 'Missed'
    : isaResultLabel(offer.status);

export const isaClaimedOffer = (workflow: IsaWorkflow): IsaOffer | undefined =>
  workflow.offers.find(offer => offer.status === 'claim_won');

export const isaFirstOfferedAt = (offers: IsaOffer[]): string | null =>
  offers.reduce<string | null>(
    (earliest, offer) =>
      earliest === null ||
      parseTimelineTimestamp(offer.offered_at) <
        parseTimelineTimestamp(earliest)
        ? offer.offered_at
        : earliest,
    null,
  );

export const isLiveTransferFollowUp = (workflow: IsaWorkflow): boolean =>
  workflow.origin === 'live_transfer';

export const isaWorkflowTitle = (workflow: IsaWorkflow): string => {
  const count = workflow.offers.length;
  if (isLiveTransferFollowUp(workflow)) {
    return `Follow-up after live transfer · ${sentenceCase(workflow.status)}`;
  }
  if (count === 0) {
    return `Visit handed to agents · ${sentenceCase(workflow.status)}`;
  }
  const outcome = workflow.claimed_by_agent_name
    ? `Claimed by ${workflow.claimed_by_agent_name}`
    : sentenceCase(workflow.status);
  return `Visit offered to ${count} agent${count === 1 ? '' : 's'} · ${outcome}`;
};

export const formatElapsed = (from: string, to: string): string | null => {
  const seconds = Math.round(
    (parseTimelineTimestamp(to) - parseTimelineTimestamp(from)) / 1000,
  );
  if (!Number.isFinite(seconds) || seconds < 0) {
    return null;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return minutes > 0 ? `${minutes}m ${seconds % 60}s` : `${seconds}s`;
};

const newestFirst = (left: string, right: string): number =>
  parseTimelineTimestamp(right) - parseTimelineTimestamp(left);

export const compareIsaOffers = (left: IsaOffer, right: IsaOffer): number => {
  const winner =
    Number(right.status === 'claim_won') - Number(left.status === 'claim_won');
  if (winner) {
    return winner;
  }
  const leftReply = isaReplyAt(left);
  const rightReply = isaReplyAt(right);
  if (leftReply && rightReply) {
    return newestFirst(leftReply, rightReply);
  }
  if (leftReply || rightReply) {
    return leftReply ? -1 : 1;
  }
  return compareText(left.agent_name ?? '', right.agent_name ?? '');
};

export const isAgentText = (text: IsaText): boolean =>
  text.from_role === 'agent';

export const isaThreadTexts = (offer: IsaOffer): IsaText[] =>
  [...offer.texts].sort(
    (left, right) =>
      parseTimelineTimestamp(left.sent_at) -
      parseTimelineTimestamp(right.sent_at),
  );

export const isaReplyAt = (offer: IsaOffer): string | null =>
  isaThreadTexts(offer).find(isAgentText)?.sent_at ?? null;

export interface IsaSystemLine {
  at: string;
  text: string;
}

export interface IsaWave {
  wave: number;
  offeredAt: string;
  offers: IsaOffer[];
}

export const isaWaves = (workflow: IsaWorkflow): IsaWave[] =>
  Array.from(new Set(workflow.offers.map(offer => offer.wave_number)))
    .map(wave => {
      const offers = workflow.offers
        .filter(offer => offer.wave_number === wave)
        .sort(compareIsaOffers);
      return {
        wave,
        offeredAt: isaFirstOfferedAt(offers) as string,
        offers,
      };
    })
    .sort((left, right) => newestFirst(left.offeredAt, right.offeredAt));

export const isaWinnerLines = (workflow: IsaWorkflow): IsaSystemLine[] => {
  const winner = isaClaimedOffer(workflow);
  return [
    ...(workflow.winner_notified_at && !winner?.texts.length
      ? [
          {
            at: workflow.winner_notified_at,
            text: `Winner text sent to ${
              workflow.claimed_by_agent_name || 'Unknown agent'
            }`,
          },
        ]
      : []),
    ...(workflow.claim_intro_sent_at
      ? [{ at: workflow.claim_intro_sent_at, text: '3-way intro sent' }]
      : []),
  ].sort((left, right) => newestFirst(left.at, right.at));
};

export const mergeTimelineEvents = (
  sources: EngagementTimelineSources,
): EngagementTimelineEvent[] => {
  const events: EngagementTimelineEvent[] = [];

  const agentName =
    sources.isaWorkflows.find(workflow => workflow.claimed_by_agent_name)
      ?.claimed_by_agent_name ?? null;
  const threeWayByConversation = new Map<string, EngagementMessage[]>();
  sources.messages.forEach(payload => {
    if (payload.text_conversation_id) {
      const group = threeWayByConversation.get(payload.text_conversation_id);
      if (group) {
        group.push(payload);
      } else {
        threeWayByConversation.set(payload.text_conversation_id, [payload]);
      }
      return;
    }
    events.push({
      source: 'message',
      timestamp: payload.created_at,
      key: timelineKey('message', payload.id, payload),
      payload,
    });
  });
  threeWayByConversation.forEach((messages, conversationId) => {
    const sorted = [...messages].sort(
      (left, right) =>
        parseTimelineTimestamp(left.created_at) -
        parseTimelineTimestamp(right.created_at),
    );
    events.push({
      source: 'threeWay',
      timestamp: sorted[0].created_at,
      key: `threeWay:${conversationId}`,
      payload: { agentName, messages: sorted },
    });
  });
  sources.calls.forEach(payload => {
    events.push({
      source: 'call',
      timestamp: payload.created_at,
      key: timelineKey('call', payload.id, payload),
      payload,
    });
  });
  sources.routing.forEach(payload => {
    events.push({
      source: 'routing',
      timestamp: payload.created_at,
      key: timelineKey('routing', payload.call_id, payload),
      payload,
    });
  });
  sources.cadenceEvaluations.forEach(payload => {
    events.push({
      source: 'cadence',
      timestamp: payload.created_at,
      key: timelineKey('cadence', payload.id, payload),
      payload,
    });
  });
  sources.ledgerFacts.forEach(payload => {
    events.push({
      source: 'ledger',
      timestamp: payload.created_at,
      key: timelineKey('ledger', payload.id, payload),
      payload,
    });
  });
  sources.notes.forEach(payload => {
    events.push({
      source: 'note',
      timestamp: payload.created_at,
      key: timelineKey('note', payload.id, payload),
      payload,
    });
  });
  sources.isaWorkflows.forEach(workflow => {
    events.push({
      source: 'isa',
      timestamp: workflow.started_at,
      key: timelineKey('isa', workflow.id, workflow),
      payload: workflow,
    });
  });

  return events
    .filter(event => Number.isFinite(parseTimelineTimestamp(event.timestamp)))
    .sort(compareTimelineEvents);
};

const compareTimelineEventsAscending = (
  left: EngagementTimelineEvent,
  right: EngagementTimelineEvent,
): number => {
  const timestampDifference =
    parseTimelineTimestamp(left.timestamp) -
    parseTimelineTimestamp(right.timestamp);
  return timestampDifference || compareText(left.key, right.key);
};

const MAX_IMMEDIATE_OUTREACH_WINDOW_MS = 24 * 60 * 60 * 1000;

export const getCadenceOutreachAssociations = (
  events: EngagementTimelineEvent[],
): Record<string, CadenceOutreachAssociation> => {
  const cadenceEvents = events
    .filter(
      (
        event,
      ): event is Extract<EngagementTimelineEvent, { source: 'cadence' }> =>
        event.source === 'cadence',
    )
    .sort(compareTimelineEventsAscending);
  const messages = events
    .filter(
      (event): event is MessageTimelineEvent =>
        event.source === 'message' && !event.payload.incoming,
    )
    .sort(compareTimelineEventsAscending);
  const calls = events
    .filter(
      (event): event is CallTimelineEvent =>
        event.source === 'call' && !isIncomingCall(event.payload),
    )
    .sort(compareTimelineEventsAscending);

  return cadenceEvents.reduce<Record<string, CadenceOutreachAssociation>>(
    (associations, evaluation, index) => {
      if (evaluation.payload.next_check_in !== 0) {
        return associations;
      }
      const start = parseTimelineTimestamp(evaluation.timestamp);
      const nextDecision = cadenceEvents[index + 1]
        ? parseTimelineTimestamp(cadenceEvents[index + 1].timestamp)
        : Number.POSITIVE_INFINITY;
      const scheduledReevaluation = evaluation.payload.next_evaluation_at
        ? parseTimelineTimestamp(evaluation.payload.next_evaluation_at)
        : Number.POSITIVE_INFINITY;
      const end = Math.min(
        nextDecision,
        scheduledReevaluation > start
          ? scheduledReevaluation
          : Number.POSITIVE_INFINITY,
        start + MAX_IMMEDIATE_OUTREACH_WINDOW_MS,
      );
      const inDecisionWindow = (event: EngagementTimelineEvent) => {
        const timestamp = parseTimelineTimestamp(event.timestamp);
        return timestamp >= start && timestamp < end;
      };
      const channel = evaluation.payload.outreach_channel;
      const expectsMessage =
        channel === 'sms' || channel === 'sms_and_phone_call';
      const expectsCall =
        channel === 'phone_call' || channel === 'sms_and_phone_call';
      if (!expectsMessage && !expectsCall) {
        return associations;
      }
      const association: CadenceOutreachAssociation = {
        expectsCall,
        expectsMessage,
      };
      if (expectsMessage) {
        association.message = messages.find(inDecisionWindow);
      }
      if (expectsCall) {
        association.call = calls.find(inDecisionWindow);
      }
      associations[evaluation.key] = association;
      return associations;
    },
    {},
  );
};

export const getRoutingAttemptKey = (attempt: RoutingAttempt): string =>
  attempt.id || stablePayloadKey(attempt);

export const compareRoutingAttempts = (
  left: RoutingAttempt,
  right: RoutingAttempt,
): number => {
  const leftTime = left.created_at
    ? parseTimelineTimestamp(left.created_at)
    : Number.NaN;
  const rightTime = right.created_at
    ? parseTimelineTimestamp(right.created_at)
    : Number.NaN;
  if (Number.isFinite(leftTime) && Number.isFinite(rightTime)) {
    const timestampDifference = leftTime - rightTime;
    if (timestampDifference !== 0) {
      return timestampDifference;
    }
  } else if (Number.isFinite(leftTime)) {
    return -1;
  } else if (Number.isFinite(rightTime)) {
    return 1;
  }
  return compareText(getRoutingAttemptKey(left), getRoutingAttemptKey(right));
};

interface ZonedDateParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
}

const getZonedDateParts = (date: Date, timezone: string): ZonedDateParts => {
  const values = new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
    timeZone: timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23',
  })
    .formatToParts(date)
    .reduce<Record<string, number>>((result, part) => {
      if (part.type !== 'literal') {
        result[part.type] = Number(part.value);
      }
      return result;
    }, {});
  return {
    year: values.year,
    month: values.month,
    day: values.day,
    hour: values.hour,
    minute: values.minute,
    second: values.second,
    millisecond: date.getUTCMilliseconds(),
  };
};

const partsToUtc = (parts: ZonedDateParts): number =>
  Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    parts.millisecond,
  );

const zonedDatePartsToTimestamp = (
  parts: ZonedDateParts,
  timezone: string,
): number => {
  const desiredTimestamp = partsToUtc(parts);
  let timestamp = desiredTimestamp;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const representedTimestamp = partsToUtc(
      getZonedDateParts(new Date(timestamp), timezone),
    );
    const difference = desiredTimestamp - representedTimestamp;
    if (difference === 0) {
      return timestamp;
    }
    timestamp += difference;
  }
  return timestamp;
};

export const getTimelineCutoff = (now: Date, timezone: string): number => {
  const current = getZonedDateParts(now, timezone);
  const targetDate = new Date(
    Date.UTC(
      current.year,
      current.month - 1,
      current.day - 30,
      current.hour,
      current.minute,
      current.second,
      current.millisecond,
    ),
  );
  return zonedDatePartsToTimestamp(
    {
      year: targetDate.getUTCFullYear(),
      month: targetDate.getUTCMonth() + 1,
      day: targetDate.getUTCDate(),
      hour: targetDate.getUTCHours(),
      minute: targetDate.getUTCMinutes(),
      second: targetDate.getUTCSeconds(),
      millisecond: targetDate.getUTCMilliseconds(),
    },
    timezone,
  );
};

export const partitionTimelineEvents = (
  events: EngagementTimelineEvent[],
  timezone: string,
  now: Date = new Date(),
): {
  recent: EngagementTimelineEvent[];
  earlier: EngagementTimelineEvent[];
} => {
  const cutoff = getTimelineCutoff(now, timezone);
  return {
    recent: events.filter(
      event => parseTimelineTimestamp(event.timestamp) >= cutoff,
    ),
    earlier: events.filter(
      event => parseTimelineTimestamp(event.timestamp) < cutoff,
    ),
  };
};
